import { useUser } from '@folklore/auth';
import { useResizeObserver } from '@folklore/hooks';
import { useUrlGenerator } from '@folklore/routes';
import classNames from 'classnames';
import { useEffect, useMemo, useState, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import useOpenedState from '../../hooks/useOpenedState';

import { useTheme, useThemeClassName } from '../../contexts/ThemeContext';
import Button from '../buttons/Button';
import LogoButton from '../buttons/LogoButton';
import ToggleButton from '../buttons/ToggleButton';
import Menu from '../menus/Menu';
import OtherLocales from './OtherLocales';

import styles from '../../../styles/partials/header-default.module.scss';

interface HeaderDefaultProps {
    menuItems?: any;
    pageUrls?: any;
    menuLabel?: ReactNode;
    position?: 'static' | 'fixed' | 'menuFloating';
    openedState?: any;
    className?: string;
    innerClassName?: string;
    contentClassName?: string;
    fixedBackgroundClassName?: string;
    fixedContentClassName?: string;
    logoButtonClassName?: string;
    logoClassName?: string;
    menuClassName?: string;
    itemsClassName?: string;
    itemClassName?: string;
    itemActiveClassName?: string;
    toggleContentClassName?: string;
    togglerClassName?: string;
    togglerOpenedClassName?: string;
    withoutOtherLocales?: boolean;
    menuChildren?: ReactNode;
    children?: ReactNode;
    containerRef: any;
}

function HeaderDefault({
    menuItems = null,
    pageUrls = null,
    menuLabel = null,
    position = 'menuFloating',
    openedState = null,
    className = null,
    innerClassName = null,
    contentClassName = null,
    fixedBackgroundClassName = null,
    fixedContentClassName = null,
    logoButtonClassName = null,
    logoClassName = null,
    menuClassName = null,
    itemsClassName = null,
    itemClassName = null,
    itemActiveClassName = null,
    toggleContentClassName = null,
    togglerClassName = null,
    togglerOpenedClassName = null,
    menuChildren = null,
    children = null,
    withoutOtherLocales = false,
    containerRef = null,
}: HeaderDefaultProps) {
    const themeClassName = useThemeClassName(styles);
    const { name: themeName, header } = useTheme();
    const {
        logo,
        withoutOtherLocales: headerWithoutLocales = false,
        withOtherLocalesInMenu = false,
    } = header || {};
    const { component: logoComponent } = logo || {};

    const { menuOpened, toggleMenu, closeMenu } = useOpenedState(openedState);

    const fixed = position === 'fixed';
    const menuFloating = position === 'menuFloating';

    const {
        ref: elRef,
        entry: { contentRect: elRect = null },
    } = useResizeObserver();
    const { height: elHeight = 0 } = elRect || {};

    const user = useUser();
    const route = useUrlGenerator();

    const innerContent = useMemo(
        () => (
            <div
                className={classNames([
                    styles.content,
                    {
                        [contentClassName]: contentClassName !== null,
                    },
                    'header-default-content',
                ])}
            >
                <LogoButton
                    logoComponent={logoComponent}
                    className={classNames([
                        styles.logoButton,
                        { [logoButtonClassName]: logoButtonClassName !== null },
                    ])}
                    logoClassName={classNames([
                        styles.logo,
                        { [logoClassName]: logoClassName !== null },
                    ])}
                    href={route(user !== null ? 'play' : 'home')}
                    onClick={closeMenu}
                />
                {!withoutOtherLocales && !headerWithoutLocales && !withOtherLocalesInMenu ? (
                    <OtherLocales
                        className={styles.otherLocales}
                        linkClassName={styles.otherLocalesLink}
                        pageUrls={pageUrls}
                    />
                ) : null}
                <ToggleButton
                    opened={menuOpened}
                    onClick={toggleMenu}
                    withLabel={menuLabel !== null}
                    className={classNames([
                        styles.toggler,
                        {
                            [togglerClassName]: togglerClassName !== null,
                            [togglerOpenedClassName]: togglerOpenedClassName !== null && menuOpened,
                        },
                        'toggler',
                    ])}
                >
                    {menuLabel !== null ? (
                        menuLabel
                    ) : (
                        <FormattedMessage defaultMessage="Menu" description="button label" />
                    )}
                </ToggleButton>
            </div>
        ),
        [
            menuLabel,
            menuOpened,
            pageUrls,
            closeMenu,
            toggleMenu,
            user,
            contentClassName,
            togglerClassName,
            togglerOpenedClassName,
        ],
    );

    const [menuFloatingHidden, setMenuFloatingHidden] = useState(false);

    useEffect(() => {
        let lastScrollY = window.scrollY;

        const onScroll = () => {
            const goingDown = lastScrollY < window.scrollY;
            setMenuFloatingHidden(goingDown && window.scrollY > elHeight);
            lastScrollY = window.scrollY;
        };

        if (menuFloating) {
            window.addEventListener('scroll', onScroll);
        }

        return () => {
            if (menuFloating) {
                window.removeEventListener('scroll', onScroll);
            }
        };
    }, [menuFloating, elHeight]);

    return (
        <header
            className={classNames([
                styles.container,
                {
                    [styles.menuOpened]: menuOpened,
                    [styles.fixed]: fixed,
                    [styles.menuFloating]: menuFloating,
                    [styles.menuFloatingHidden]: menuFloatingHidden,
                },
                themeClassName,
                className,
                'header-default',
            ])}
            ref={(ref) => {
                elRef.current = ref;
                if (containerRef !== null) {
                    containerRef.current = ref;
                }
            }}
        >
            <Button className={styles.menuSafe} onClick={closeMenu} />
            <div
                className={classNames([
                    styles.fixedBackground,
                    { [fixedBackgroundClassName]: fixedBackgroundClassName !== null },
                ])}
            >
                {innerContent}
            </div>
            <div className={styles.floatingContent}>{innerContent}</div>
            <div
                className={classNames([
                    styles.toggleContent,
                    { [toggleContentClassName]: toggleContentClassName !== null },
                ])}
            >
                <Menu
                    className={classNames([
                        styles.menu,
                        { [menuClassName]: menuClassName !== null },
                        'menu',
                    ])}
                    itemsClassName={classNames([
                        styles.items,
                        { [itemsClassName]: itemsClassName !== null },
                        'items',
                    ])}
                    itemClassName={classNames([
                        styles.item,
                        { [itemClassName]: itemClassName !== null },
                        'item',
                    ])}
                    itemActiveClassName={classNames([
                        styles.itemActive,
                        { [itemActiveClassName]: itemActiveClassName !== null },
                        'item-active',
                    ])}
                    items={menuItems}
                    onItemClick={closeMenu}
                />
                {menuChildren}
                {!withoutOtherLocales && !headerWithoutLocales && withOtherLocalesInMenu ? (
                    <OtherLocales
                        className={styles.menuOtherLocales}
                        linkClassName={styles.menuOtherLocale}
                        pageUrls={pageUrls}
                    />
                ) : null}
            </div>
            <div
                className={classNames([
                    styles.inner,
                    { [innerClassName]: innerClassName !== null },
                ])}
            >
                {innerContent}
            </div>
            {themeName !== 'rhoneacademy' ? (
                <div
                    className={classNames([
                        styles.fixedContent,
                        { [fixedContentClassName]: fixedContentClassName !== null },
                    ])}
                >
                    {innerContent}
                </div>
            ) : null}
            {children}
        </header>
    );
}

export default HeaderDefault;
